import CustomLoadingButton from 'Components/CustomLoadingButton'
import FormTextField from 'Components/FormTextField'
import StyledForm from 'Components/StyledForm'
import {
  useCreateSingleCodespaceMutation,
  useUpdateSingleCodespaceMutation
} from 'Services/ControlDataManagement/Features/Codespace/codespaceApi'
import { SELECTED_CODESPACE_PARAMETER_KEY } from 'Services/ControlDataManagement/Features/Codespace/Hooks/useCodespacesWithParams'
import type { Codespace } from 'Services/ControlDataManagement/Features/Codespace/types'
import type { FieldValidationErrors } from 'Services/ControlDataManagement/types'
import type { ControlDataServiceError } from 'Util/ApiClient'
import { useValidationErrors } from 'Util/hooks/useValidationErrors'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { Box, Grid } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: FieldValidationErrors = {
  codespace_id: [],
  description: []
}

interface CodespaceFormProps {
  successBaseUrl: string
  submitLoading: boolean
  codespace?: Codespace
}

export default function CodespaceForm({
  codespace,
  successBaseUrl,
  submitLoading
}: Readonly<CodespaceFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!codespace

  const { setValidationErrors, getErrorProps } = useValidationErrors(fieldErrorsDefaultState)

  const [createSingleCodespace, { isLoading: createLoading }] = useCreateSingleCodespaceMutation()
  const [updateSingleCodespace, { isLoading: updateLoading }] = useUpdateSingleCodespaceMutation()

  const [codespaceDetails, setCodespaceDetails] = useState<Partial<Codespace>>(
    (isEdit && codespace) || {}
  )

  const handleChange = (value: string | undefined, field: keyof Codespace) => {
    if (value !== undefined) {
      setCodespaceDetails(prevState => ({ ...prevState, [field]: value }) as Codespace)
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const codespaceData = {
      ...codespaceDetails
    }

    if (codespace) {
      updateSingleCodespace({
        codespace_id: codespace.codespace_id,
        ...codespaceData
      })
        .unwrap()
        .then(() => {
          SnackbarUtils.success(t`Codespace successfully updated.`)
          navigate(successBaseUrl)
        })
        .catch(({ data: errorResponse }: { data: ControlDataServiceError }) => {
          const { violations } = errorResponse
          setValidationErrors(violations)
          SnackbarUtils.error(t`There was an error updating the codespace.`)
        })
      return
    }

    createSingleCodespace(codespaceData)
      .unwrap()
      .then((codespaceResponse: Codespace) => {
        SnackbarUtils.success(t`Codespace successfully created.`)
        navigate(
          appendQueryParamsToUrl(successBaseUrl, {
            [SELECTED_CODESPACE_PARAMETER_KEY]: codespaceResponse.codespace_id
          })
        )
      })
      .catch(({ data: errorResponse }: { data: ControlDataServiceError }) => {
        const { violations } = errorResponse
        setValidationErrors(violations)
        SnackbarUtils.error(t`There was an error creating the codespace.`)
      })
  }

  const isSubmitLoading = submitLoading || createLoading || updateLoading

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
          <Box sx={{ display: 'flex' }}>
            <FormTextField
              name="codespace_id"
              required
              label={t`Codespace identifier`}
              value={codespaceDetails?.codespace_id ?? ''}
              maxLength={40}
              onChange={e => handleChange(e.target.value, 'codespace_id')}
              {...getErrorProps('codespace_id')}
            />
          </Box>
          <Box>
            <FormTextField
              name="description"
              required
              label={t`Description`}
              value={codespaceDetails?.description ?? ''}
              onChange={e => handleChange(e.target.value, 'description')}
              multiline
              {...getErrorProps('description')}
            />
          </Box>
          <Box sx={{ py: 4 }}>
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Save codespace`}
            </CustomLoadingButton>
          </Box>
        </StyledForm>
      </Grid>
    </Grid>
  )
}
